import * as React from 'react';
import { connect } from 'react-redux';
import { find } from 'lodash';
import * as minuteDiff from 'minute-diff';
import {
  Async,
  Avatar,
  Button,
  ButtonGroup,
  Card,
  DateTime,
  IconButton,
  Notification,
  Subtitle,
  Tag,
  Title,
  Permission,
  PermissionForm,
  PermissionList,
  AccountForm,
  AccountList,
  RouteWrapper,
} from '../../components';
import {
  getUser,
  getUserPermissions,
  getUserAccounts,
  removePermissionFromUser,
  removeAccountFromUser,
  addDefaultAccountToUser,
} from '../../redux/actions/users';

interface Props {
  users: any;
  match: any;
  dispatch: Function;
}

interface State {
  id: number;
  user: any;
  ready: boolean;
  section: number;
}

class Route extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    const id = parseInt(this.props.match.params.id, 10);
    this.state = {
      id,
      user: find(this.props.users.users, { id }) || false,
      ready: false,
      section: 0,
    };
    this.handlePermissionRemove = this.handlePermissionRemove.bind(this);
    this.handleAccountRemove = this.handleAccountRemove.bind(this);
    this.renderHeading = this.renderHeading.bind(this);
    this.renderDelete = this.renderDelete.bind(this);
    this.renderBody = this.renderBody.bind(this);
    this.renderPermissions = this.renderPermissions.bind(this);
    this.renderAccounts = this.renderAccounts.bind(this);
    this.renderSectionButtons = this.renderSectionButtons.bind(this);
  }

  componentDidMount() {
    const onReady = () => {
      this.props.dispatch(getUserAccounts(this.state.id));
      this.props.dispatch(getUserPermissions(this.state.id));
      this.setState({ ready: true });
    };
    if (
      !this.state.user.lastUpdated ||
      minuteDiff(this.state.user.lastUpdated) >= 1
    ) {
      this.props.dispatch(getUser(this.state.id)).then((response) => {
        this.setState({ user: response.data });
        onReady();
      });
    } else {
      onReady();
    }
  }

  handlePermissionRemove(name) {
    this.props.dispatch(removePermissionFromUser(this.state.id, name));
  }

  handleAccountRemove(name) {
    this.props.dispatch(removeAccountFromUser(this.state.id, name));
  }

  renderHeading() {
    return (
      <div className="level-left">
        {this.state.user.email && <Avatar email={this.state.user.email} />}
        <Async on={this.state.user.email}>
          <Title>{this.state.user.name || 'Unknown User'}</Title>
          <Subtitle>{this.state.user.email}</Subtitle>
        </Async>
      </div>
    );
  }

  renderDelete() {
    if (this.state.user.id === 1) {
      return null;
    }
    return (
      <Permission require="MANAGE_DELETIONS">
        <div className="level-right">
          <IconButton
            link={`/settings/users/${this.state.id}/delete`}
            className="is-danger"
            icon="trash"
          >
            Delete
          </IconButton>
        </div>
      </Permission>
    );
  }

  renderBody() {
    return (
      <Card>
        <table>
          <tbody>
            <tr>
              <th>User ID</th>
              <td>
                <Tag type="default">{this.state.id}</Tag>
              </td>
            </tr>
            <tr>
              <th>Name</th>
              <td>
                <Async on={this.state.user.email}>
                  {this.state.user.name || 'N/A'}
                </Async>
              </td>
            </tr>
            <tr>
              <th>Email</th>
              <td>
                <Async on={this.state.user.email}>
                  {this.state.user.email}
                  &nbsp;&nbsp;
                  {this.state.user.active ? (
                    <Tag>Verified</Tag>
                  ) : (
                    <Tag type="danger">Unconfirmed</Tag>
                  )}
                </Async>
              </td>
            </tr>
            <tr>
              <th>Registration Date</th>
              <td>
                <Async on={this.state.user.created_at}>
                  <DateTime date={this.state.user.created_at} />
                </Async>
              </td>
            </tr>
          </tbody>
        </table>
      </Card>
    );
  }

  /**
   * Display buttons for switching between user sections
   */
  renderSectionButtons() {
    return (
      <ButtonGroup>
        {['Overview', 'Permissions', 'Sites'].map((name, index) => (
          <Button
            key={name}
            className={
              this.state.section === index ? 'is-primary' : 'is-default'
            }
            handleClick={() => {
              this.setState({ section: index });
            }}
          >
            {name}
          </Button>
        ))}
      </ButtonGroup>
    );
  }

  /**
   * Render the section to modify the user's permissions
   */
  renderPermissions() {
    return (
      <Permission require="MANAGE_PERMISSIONS">
        <div className="container">
          {this.state.user.id !== 1 ? (
            <div className="columns">
              <div className="column">
                <h4 className="heading">Assigned</h4>
                {this.state.user.permissions && (
                  <PermissionList
                    permissions={this.state.user.permissions}
                    handleDelete={this.handlePermissionRemove}
                  />
                )}
              </div>
              <div className="column is-5">
                <h4 className="heading">Add Permission</h4>
                <PermissionForm userId={this.state.id} />
              </div>
            </div>
          ) : (
            <Notification type="danger">
              You may not manage permissions for this user.
            </Notification>
          )}
        </div>
      </Permission>
    );
  }

  /**
   * Render the section to modify the user's accounts
   */
  renderAccounts() {
    return (
      <div className="container">
        {this.state.user.id !== 1 ? (
          <div className="columns">
            <div className="column">
              <h4 className="heading">Assigned</h4>
              {this.state.user.accounts && (
                <AccountList
                  accounts={this.state.user.accounts}
                  handleDefault={(id) => {
                    this.props.dispatch(
                      addDefaultAccountToUser(this.state.id, id),
                    );
                  }}
                  handleDelete={this.handleAccountRemove}
                />
              )}
            </div>
            <div className="column is-5">
              <h4 className="heading">Add Site</h4>
              <AccountForm userId={this.state.id} />
            </div>
          </div>
        ) : (
          <Notification type="danger">
            You may not manage sites for this user.
          </Notification>
        )}
      </div>
    );
  }

  render() {
    let Section = null;
    switch (this.state.section) {
      case 0:
        Section = this.renderBody;
        break;
      case 1:
        Section = this.renderPermissions;
        break;
      case 2:
        Section = this.renderAccounts;
        break;
    }
    return (
      <RouteWrapper
        title={this.state.user.name || 'Unknown User'}
        requireAuth
        requirePermission="MANAGE_USERS"
        breadcrumbs={[
          { link: '/settings', text: 'Settings' },
          { link: '/settings/users', text: 'Users' },
          {
            link: `/settings/users/${this.state.id}`,
            text: this.state.user.name || 'User',
          },
        ]}
        loading={this.props.users.isFetching}
        error={!this.state.ready && this.props.users.error}
      >
        <div className="container">
          <div className="level">
            {this.renderHeading()}
            {this.renderDelete()}
          </div>
          {this.renderSectionButtons()}
          <Section />
        </div>
      </RouteWrapper>
    );
  }
}

const mapStateToProps = ({ users }) => ({
  users,
});

export default connect(mapStateToProps)(Route);
